/**
 * Circuit breaker pattern implementation for ludicrous-gateway
 * Prevents cascading failures by temporarily stopping requests to unhealthy providers
 */

import { getLogger, type Logger } from "../utils/logger.js";

/**
 * Circuit breaker states
 */
export type CircuitState = "CLOSED" | "OPEN" | "HALF_OPEN";

/**
 * Circuit breaker configuration
 */
export interface CircuitBreakerConfig {
  /** Number of failures before opening circuit */
  failureThreshold: number;
  /** Milliseconds to wait before trying half-open */
  recoveryTimeoutMs: number;
  /** Number of successful requests in half-open to close circuit */
  successThreshold?: number;
}

/**
 * Circuit breaker statistics
 */
export interface CircuitStats {
  state: CircuitState;
  failures: number;
  successes: number;
  lastFailureTime: Date | null;
  lastSuccessTime: Date | null;
  lastStateChange: Date;
  totalTrips: number;
}

/**
 * Circuit breaker for a single provider
 */
export class CircuitBreaker {
  private state: CircuitState = "CLOSED";
  private failures: number = 0;
  private successes: number = 0;
  private lastFailureTime: Date | null = null;
  private lastSuccessTime: Date | null = null;
  private lastStateChange: Date = new Date();
  private totalTrips: number = 0;
  private config: Required<CircuitBreakerConfig>;
  private logger: Logger;

  readonly name: string;

  constructor(
    name: string,
    config: CircuitBreakerConfig
  ) {
    this.name = name;
    this.config = {
      failureThreshold: config.failureThreshold,
      recoveryTimeoutMs: config.recoveryTimeoutMs,
      successThreshold: config.successThreshold ?? 1,
    };
    this.logger = getLogger().child({ circuitBreaker: name });
  }

  /**
   * Check if the circuit allows requests
   */
  isAllowed(): boolean {
    this.checkStateTransition();

    switch (this.state) {
      case "CLOSED":
        return true;
      case "OPEN":
        return false;
      case "HALF_OPEN":
        // Allow limited requests in half-open state
        return true;
    }
  }

  /**
   * Record a successful request
   */
  recordSuccess(): void {
    this.successes++;
    this.lastSuccessTime = new Date();

    if (this.state === "HALF_OPEN") {
      if (this.successes >= this.config.successThreshold) {
        this.transitionTo("CLOSED");
        this.failures = 0;
        this.successes = 0;
      }
    } else if (this.state === "CLOSED") {
      // Reset failures on success in closed state
      this.failures = 0;
    }
  }

  /**
   * Record a failed request
   */
  recordFailure(): void {
    this.failures++;
    this.lastFailureTime = new Date();

    if (this.state === "HALF_OPEN") {
      // Any failure in half-open goes back to open
      this.transitionTo("OPEN");
      this.successes = 0;
    } else if (this.state === "CLOSED") {
      if (this.failures >= this.config.failureThreshold) {
        this.transitionTo("OPEN");
        this.totalTrips++;
      }
    }
  }

  /**
   * Get current circuit state
   */
  getState(): CircuitState {
    this.checkStateTransition();
    return this.state;
  }

  /**
   * Get circuit statistics
   */
  getStats(): CircuitStats {
    this.checkStateTransition();
    return {
      state: this.state,
      failures: this.failures,
      successes: this.successes,
      lastFailureTime: this.lastFailureTime,
      lastSuccessTime: this.lastSuccessTime,
      lastStateChange: this.lastStateChange,
      totalTrips: this.totalTrips,
    };
  }

  /**
   * Force circuit to a specific state (for testing/admin)
   */
  forceState(state: CircuitState): void {
    this.transitionTo(state);
    if (state === "CLOSED") {
      this.failures = 0;
      this.successes = 0;
    }
    this.logger.warn(`Circuit forced to ${state}`);
  }

  /**
   * Reset the circuit breaker
   */
  reset(): void {
    this.state = "CLOSED";
    this.failures = 0;
    this.successes = 0;
    this.lastFailureTime = null;
    this.lastSuccessTime = null;
    this.lastStateChange = new Date();
    this.logger.info("Circuit breaker reset");
  }

  /**
   * Check if state should transition based on time
   */
  private checkStateTransition(): void {
    if (this.state !== "OPEN") {
      return;
    }

    const now = Date.now();
    const timeSinceLastChange = now - this.lastStateChange.getTime();

    if (timeSinceLastChange >= this.config.recoveryTimeoutMs) {
      this.transitionTo("HALF_OPEN");
      this.successes = 0;
    }
  }

  /**
   * Transition to a new state
   */
  private transitionTo(newState: CircuitState): void {
    if (this.state !== newState) {
      this.logger.info(`Circuit state: ${this.state} -> ${newState}`);
      this.state = newState;
      this.lastStateChange = new Date();
    }
  }
}

/**
 * Circuit breaker manager for multiple providers
 */
export class CircuitBreakerManager {
  private breakers: Map<string, CircuitBreaker> = new Map();
  private defaultConfig: CircuitBreakerConfig;

  constructor(defaultConfig: CircuitBreakerConfig) {
    this.defaultConfig = defaultConfig;
  }

  /**
   * Get or create a circuit breaker for a provider
   */
  getBreaker(providerName: string): CircuitBreaker {
    let breaker = this.breakers.get(providerName);
    if (!breaker) {
      breaker = new CircuitBreaker(providerName, this.defaultConfig);
      this.breakers.set(providerName, breaker);
    }
    return breaker;
  }

  /**
   * Check if a provider is allowed
   */
  isAllowed(providerName: string): boolean {
    return this.getBreaker(providerName).isAllowed();
  }

  /**
   * Record success for a provider
   */
  recordSuccess(providerName: string): void {
    this.getBreaker(providerName).recordSuccess();
  }

  /**
   * Record failure for a provider
   */
  recordFailure(providerName: string): void {
    this.getBreaker(providerName).recordFailure();
  }

  /**
   * Get all circuit breaker stats
   */
  getAllStats(): Map<string, CircuitStats> {
    const stats = new Map<string, CircuitStats>();
    for (const [name, breaker] of this.breakers) {
      stats.set(name, breaker.getStats());
    }
    return stats;
  }

  /**
   * Reset all circuit breakers
   */
  resetAll(): void {
    for (const breaker of this.breakers.values()) {
      breaker.reset();
    }
  }
}
